import { useState, useEffect } from 'react';
import type { ButtonHTMLAttributes } from 'react';
import { Modal, useModalClose } from '@/components/ui/Modal';
import { api } from '../../utils/api';
import { formatDate } from '../../utils/formatters';
import { Users, Plus, X } from 'lucide-react';

interface TeamMember {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  role: { displayName: string; name: string };
}

export function TeamPage() {
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  const load = () => {
    setLoading(true);
    api.get('/admin/team')
      .then(res => setTeam(res.data.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Ardhanex Team</h1>
          <p className="text-sm text-gray-500">Internal platform users (no tenant assignment)</p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)]"
        >
          <Plus size={16} /> Add Team Member
        </button>
      </div>

      {loading ? (
        <div className="flex h-32 items-center justify-center">
          <div className="h-6 w-6 animate-spin rounded-full border-4 border-[var(--ink-0)] border-t-transparent" />
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="px-4 py-3 font-medium text-gray-500">Name</th>
                <th className="px-4 py-3 font-medium text-gray-500">Email</th>
                <th className="px-4 py-3 font-medium text-gray-500">Role</th>
                <th className="px-4 py-3 font-medium text-gray-500">Status</th>
                <th className="px-4 py-3 font-medium text-gray-500">Last Login</th>
                <th className="px-4 py-3 font-medium text-gray-500">Joined</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {team.map(m => (
                <tr key={m.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">{m.firstName} {m.lastName}</td>
                  <td className="px-4 py-3 text-gray-600">{m.email}</td>
                  <td className="px-4 py-3 text-gray-600">{m.role.displayName}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                      m.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {m.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs">
                    {m.lastLoginAt ? formatDate(m.lastLoginAt) : 'Never'}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{formatDate(m.createdAt)}</td>
                </tr>
              ))}
              {team.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                    <Users size={24} className="mx-auto mb-2 text-gray-300" />
                    No team members found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showAdd && <AddTeamMemberModal onClose={() => { setShowAdd(false); load(); }} />}
    </div>
  );
}

function AddTeamMemberModal({ onClose }: { onClose: () => void }) {
  const [form, setForm] = useState({ email: '', password: '', firstName: '', lastName: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await api.post('/admin/team', form);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to add team member');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open onClose={onClose} className="!max-w-md !rounded-2xl !bg-white">
      <div>
        <div className="flex items-center justify-between p-6 pb-4 border-b border-gray-200 sticky top-0 z-10 bg-white rounded-t-2xl">
          <h2 className="text-lg font-bold text-gray-900">Add Team Member</h2>
          <RequestCloseButton className="rounded p-1 hover:bg-gray-100">
            <X size={20} className="text-gray-500" />
          </RequestCloseButton>
        </div>
        <div className="p-6 pt-4">

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{error}</div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">First Name</label>
              <input
                required
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                value={form.firstName}
                onChange={e => setForm(p => ({ ...p, firstName: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Last Name</label>
              <input
                required
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                value={form.lastName}
                onChange={e => setForm(p => ({ ...p, lastName: e.target.value }))}
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
            <input
              required
              type="email"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
              value={form.email}
              onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
              placeholder="name@ardhanex.com"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Password</label>
            <input
              required
              type="password"
              minLength={6}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
              value={form.password}
              onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
              placeholder="Min 6 characters"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <RequestCloseButton
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50"
            >
              Cancel
            </RequestCloseButton>
            <button
              type="submit"
              disabled={submitting}
              className="rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
            >
              {submitting ? 'Adding...' : 'Add Member'}
            </button>
          </div>
        </form>
        </div>
      </div>
    </Modal>
  );
}

function RequestCloseButton({ children, ...props }: ButtonHTMLAttributes<HTMLButtonElement>) {
  const { requestClose } = useModalClose();
  return <button type="button" {...props} onClick={requestClose}>{children}</button>;
}
